import { getCompanyOrgStructure } from '@commenda/api';
import type { ReactFlowInstance } from '@xyflow/react';
import { createElement } from 'react';
import { createRoot } from 'react-dom/client';
import { captureChart } from '../OrgDiagram/exportOrgChart';
import { OrgDiagram } from '../OrgDiagram';
import { TaxDiagram } from '../TaxDiagram';
import { captureTaxChart } from '../TaxDiagram/exportTaxChart';

// Off-screen mount size — wide enough for typical org charts to lay out
// without compression. The captured PNG size is driven by the chart's
// own bounding box (captureChart re-translates by PADDING), not by these.
const HOST_WIDTH = 2200;
const HOST_HEIGHT = 1600;

const CAPTURE_TIMEOUT_MS = 30_000;

// Renders the diagram in a hidden offscreen mount, waits for nodes to
// land, captures via the same html-to-image pipeline as the on-screen
// export, then unmounts. Returns null when the company has no chartable
// entities (no point embedding a blank canvas).
export async function captureCompanyChartPng(opts: {
  companyId: number;
  kind: 'org' | 'tax';
}): Promise<string | null> {
  const structure = await getCompanyOrgStructure(opts.companyId);
  if (structure.entities.length === 0) return null;

  const host = document.createElement('div');
  host.setAttribute('aria-hidden', 'true');
  host.style.cssText = [
    'position:fixed',
    'left:-99999px',
    'top:0',
    `width:${HOST_WIDTH}px`,
    `height:${HOST_HEIGHT}px`,
    'pointer-events:none',
    'contain:strict',
  ].join(';');
  document.body.appendChild(host);

  const root = createRoot(host);
  let cleaned = false;
  const cleanup = () => {
    if (cleaned) return;
    cleaned = true;
    // Defer so React isn't unmounting mid-effect.
    queueMicrotask(() => {
      try {
        root.unmount();
      } catch {
        /* ignore */
      }
      host.remove();
    });
  };

  return new Promise<string | null>((resolve, reject) => {
    const settle = (action: () => void) => {
      window.clearTimeout(timer);
      cleanup();
      action();
    };
    const timer = window.setTimeout(
      () => settle(() => reject(new Error('Chart capture timed out'))),
      CAPTURE_TIMEOUT_MS,
    );

    const Diagram = opts.kind === 'tax' ? TaxDiagram : OrgDiagram;
    // Tax kind composites the static legend at bottom-left after capture; org
    // kind has no legend, so it goes through captureChart directly.
    const capture = opts.kind === 'tax' ? captureTaxChart : captureChart;
    const handleReady = (rf: ReactFlowInstance) => {
      // Transparent background mirrors the on-toolbar "Export PNG" flow —
      // the PDF page is white anyway, and the smaller PNG shrinks the
      // request payload significantly.
      capture(rf, undefined, host).then(
        ({ dataUrl }) => settle(() => resolve(dataUrl)),
        (err) => settle(() => reject(err)),
      );
    };

    root.render(
      createElement(Diagram, {
        structure,
        companyId: opts.companyId,
        hideUnlinkedPanel: true,
        hideToolbar: true,
        onReady: handleReady,
      }),
    );
  });
}
